<template>
  <a-modal
    :visible="visible"
    :width="520"
    title="Filtrar e ordenar"
    wrap-class-name="itineraries-filter-modal"
    @cancel="$emit('close')"
  >
    <div class="itineraries-filter-modal__section">
      <h4 class="itineraries-filter-modal__label">Ordenar por</h4>

      <a-radio-group v-model="draft.ordenar">
        <a-radio value="az">A-Z</a-radio>
        <a-radio value="za">Z-A</a-radio>
      </a-radio-group>
    </div>

    <div class="itineraries-filter-modal__section">
      <h4 class="itineraries-filter-modal__label">Status</h4>

      <a-radio-group
        v-model="draft.status"
        class="itineraries-filter-modal__stacked"
      >
        <a-radio value="ativos">Ativo</a-radio>
        <a-radio value="inativos">Inativo</a-radio>
      </a-radio-group>
    </div>

    <div class="itineraries-filter-modal__section">
      <h4 class="itineraries-filter-modal__label">Fornecedor (parada)</h4>

      <a-select
        v-model="draft.suppliers"
        mode="multiple"
        label-in-value
        placeholder="Digite o nome ou CNPJ da parada"
        class="itineraries-filter-modal__suppliers"
        :filter-option="false"
        :loading="suppliersLoading"
        :not-found-content="notFoundContent"
        @search="onSupplierSearch"
      >
        <a-select-option
          v-for="option in supplierOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ supplierLabel(option) }}
        </a-select-option>
      </a-select>
    </div>

    <template slot="footer">
      <a-button @click="handleClear">Limpar filtro</a-button>
      <a-button type="primary" :disabled="!isDirty" @click="handleApply">
        Aplicar
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import { mapActions } from "vuex";
import debounce from "lodash/debounce";
import { handleApiError } from "@/modules/Itineraries/utils/handleApiError";

const SUPPLIER_SEARCH_MIN_LENGTH = 3;

const SORT_BY_OPTION = {
  az: "descr|asc",
  za: "descr|desc",
};

function supplierKeys(suppliers) {
  return (suppliers || [])
    .map((item) => Number(item.key))
    .sort((a, b) => a - b)
    .join(",");
}

function buildDraft(filters, suppliers) {
  const source = filters || {};
  let ordenar = null;
  if (source.sort === SORT_BY_OPTION.az) ordenar = "az";
  if (source.sort === SORT_BY_OPTION.za) ordenar = "za";

  let status = null;
  if (source.status === "ativos") status = "ativos";
  if (source.status === "inativos") status = "inativos";

  return { ordenar, status, suppliers: (suppliers || []).slice() };
}

export default {
  name: "ItinerariesFilterModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    suppliers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      draft: buildDraft(this.filters, this.suppliers),
      supplierOptions: [],
      suppliersLoading: false,
      supplierTerm: "",
    };
  },
  computed: {
    isDirty() {
      const applied = buildDraft(this.filters, this.suppliers);
      return (
        this.draft.ordenar !== applied.ordenar ||
        this.draft.status !== applied.status ||
        supplierKeys(this.draft.suppliers) !== supplierKeys(applied.suppliers)
      );
    },
    notFoundContent() {
      if (this.suppliersLoading) return "Buscando...";
      if (this.supplierTerm.length < SUPPLIER_SEARCH_MIN_LENGTH) {
        return `Digite ao menos ${SUPPLIER_SEARCH_MIN_LENGTH} caracteres`;
      }
      return "Nenhuma parada encontrada";
    },
  },
  created() {
    this.debouncedSupplierSearch = debounce(this.loadSuppliers, 500);
  },
  beforeDestroy() {
    this.debouncedSupplierSearch.cancel();
  },
  watch: {
    visible(isVisible) {
      if (!isVisible) return;
      this.draft = buildDraft(this.filters, this.suppliers);
      this.supplierOptions = [];
      this.supplierTerm = "";
    },
  },
  methods: {
    ...mapActions("itineraries", ["searchSuppliers"]),
    supplierLabel(option) {
      return option.documento
        ? `${option.label} — ${option.documento}`
        : option.label;
    },
    onSupplierSearch(term) {
      this.supplierTerm = String(term || "").trim();

      if (this.supplierTerm.length < SUPPLIER_SEARCH_MIN_LENGTH) {
        this.debouncedSupplierSearch.cancel();
        this.supplierOptions = [];
        this.suppliersLoading = false;
        return;
      }

      this.suppliersLoading = true;
      this.debouncedSupplierSearch();
    },
    async loadSuppliers() {
      const term = this.supplierTerm;

      try {
        const options = await this.searchSuppliers({ search: term });
        if (term !== this.supplierTerm) return;
        this.supplierOptions = options;
      } catch (error) {
        this.supplierOptions = [];
        handleApiError(this, error, "Não foi possível buscar as paradas");
      } finally {
        if (term === this.supplierTerm) this.suppliersLoading = false;
      }
    },
    handleApply() {
      if (!this.isDirty) return;
      this.$emit("apply", {
        sort: this.draft.ordenar ? SORT_BY_OPTION[this.draft.ordenar] : null,
        status: this.draft.status || "todos",
        suppliers: this.draft.suppliers.slice(),
      });
    },
    handleClear() {
      this.draft = { ordenar: null, status: null, suppliers: [] };
      this.$emit("clear");
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.itineraries-filter-modal__section {
  padding: 16px 0;

  &:first-child {
    padding-top: 0;
  }

  & + & {
    border-top: 1px solid @color-bg-subtle;
  }
}

.itineraries-filter-modal__label {
  font-size: 14px;
  font-weight: 600;
  color: @color-text-dark;
  margin: 0 0 12px 0;
}

.itineraries-filter-modal__suppliers {
  width: 100%;
}

.itineraries-filter-modal__stacked {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>

<style lang="less">
@import "~@/shared/styles/variables.less";

.itineraries-filter-modal {
  .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
    color: @color-text-dark;
  }

  .ant-modal-body {
    padding: 24px;
  }

  .ant-radio-checked .ant-radio-inner {
    border-color: @color-primary;

    &::after {
      background-color: @color-primary;
    }
  }

  .ant-btn-primary:not([disabled]) {
    background-color: @color-primary;
    border-color: @color-primary;

    &:hover,
    &:focus {
      background-color: @color-primary-hover;
      border-color: @color-primary-hover;
    }
  }
}
</style>
